import React, { useEffect, useRef } from 'react';
import {
  Box,
  Progress,
  Text,
  HStack,
  Fade
} from '@chakra-ui/react';

const STATUS_COLORS = {
  campaign:          { bar: 'blue',   accent: 'blue.300'   },
  linkedin_profiles: { bar: 'blue',   accent: 'blue.300'   },
  custom_fields:     { bar: 'purple', accent: 'purple.300' },
  email_drafts:      { bar: 'cyan',   accent: 'cyan.300'   },
  crm_import:        { bar: 'orange', accent: 'orange.300' },
};

const LABELS = {
  custom_fields:     'Custom Fields',
  linkedin_profiles: 'LinkedIn profiles',
  email_drafts:      'Email Drafts',
  crm_import:        'Importing',
  campaign:          'Processing',
};

const CampaignStatusBar = ({
  statusUpdate,
  onComplete,
  operationType = 'campaign',
}) => {
  const hasCompletedRef = useRef(false);
  const autoHideTimeoutRef = useRef(null);
  const lastCompletedOpRef = useRef(null);

  useEffect(() => {
    if (!statusUpdate) {
      hasCompletedRef.current = false;
      lastCompletedOpRef.current = null;
      if (autoHideTimeoutRef.current) {
        clearTimeout(autoHideTimeoutRef.current);
        autoHideTimeoutRef.current = null;
      }
      return;
    }
    const opId = statusUpdate.operation_id;
    if (opId && opId !== lastCompletedOpRef.current) {
      hasCompletedRef.current = false;
    }
  }, [statusUpdate]);

  useEffect(() => {
    if (!statusUpdate) return;
    const progress = parseInt(statusUpdate.completion_percentage) || 0;
    if (progress >= 100 && onComplete && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      lastCompletedOpRef.current = statusUpdate.operation_id ?? null;
      onComplete(statusUpdate.operation_id);
    }
  }, [statusUpdate, onComplete]);

  useEffect(() => {
    return () => {
      if (autoHideTimeoutRef.current) clearTimeout(autoHideTimeoutRef.current);
    };
  }, []);

  if (!statusUpdate) return null;

  const progress = parseInt(statusUpdate.completion_percentage) || 0;
  const message = statusUpdate.message || 'Processing…';
  const isComplete = progress >= 100;

  const colors = isComplete
    ? { bar: 'green', accent: 'green.300' }
    : (STATUS_COLORS[operationType] || STATUS_COLORS.campaign);
  const label = LABELS[operationType] || LABELS.campaign;

  return (
    <Fade in={true}>
      <Box
        bg="rgba(18, 18, 24, 0.85)"
        backdropFilter="blur(12px)"
        border="1px solid rgba(255, 255, 255, 0.06)"
        borderRadius="10px"
        px={4}
        py={3}
        w="100%"
        boxShadow="0 4px 20px rgba(0, 0, 0, 0.4)"
      >
        <HStack justify="space-between" mb="6px">
          <Text
            fontSize="10px"
            fontWeight="600"
            letterSpacing="0.04em"
            textTransform="uppercase"
            color={colors.accent}
          >
            {label}
          </Text>
          <Text fontSize="10px" fontWeight="500" color="whiteAlpha.500">
            {progress}%
          </Text>
        </HStack>

        <Text fontSize="11px" color="whiteAlpha.700" mb="8px" noOfLines={1}>
          {message}
        </Text>

        <Progress
          value={progress}
          colorScheme={colors.bar}
          size="xs"
          borderRadius="4px"
          bg="rgba(255, 255, 255, 0.06)"
          w="100%"
        />
      </Box>
    </Fade>
  );
};

export default CampaignStatusBar; 